import React from 'react';

const DocsPage = () => {
    return (
        <div className="min-vh-100 bg-light">
            {/* Header */}
            <div style={{ backgroundColor: '#2d5f5d' }} className="text-white py-5">
                <div className="container">
                    <h1 className="display-5 fw-bold mb-2">Documentation</h1>
                    <p className="text-white-50">Learn how to manage your restaurant menu</p>
                </div>
            </div>

            {/* Content */}
            <div className="container py-5">

                {/* Getting Started */}
                <section className="mb-5">
                    <h2 className="h3 fw-bold mb-4">Getting Started</h2>
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <h3 className="h5 fw-semibold mb-3">Accessing Your Business</h3>
                            <ol className="list-group list-group-numbered list-group-flush">
                                <li className="list-group-item border-0 ps-0">
                                    Click the "Open CMS" button from your profile page
                                </li>
                                <li className="list-group-item border-0 ps-0">
                                    You'll be automatically logged into your businesses content management system
                                </li>
                                <li className="list-group-item border-0 ps-0">
                                    Look for the "Menu Items" collection in the left sidebar
                                </li>
                            </ol>
                        </div>
                    </div>
                </section>

                {/* Managing Menu Items */}
                <section className="mb-5">
                    <h2 className="h3 fw-bold mb-4">Managing Menu Items</h2>

                    {/* Adding New Items */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <h3 className="h5 fw-semibold mb-3">Adding a New Menu Item</h3>
                            <ol className="list-group list-group-numbered list-group-flush">
                                <li className="list-group-item border-0 ps-0">
                                    Navigate to "Menu Items" in the sidebar
                                </li>
                                <li className="list-group-item border-0 ps-0">
                                    Click the "+" button in the top right
                                </li>
                                <li className="list-group-item border-0 ps-0">
                                    Fill in the required fields (name, description, price)
                                </li>
                                <li className="list-group-item border-0 ps-0">
                                    Select the appropriate item type (see below for type-specific fields)
                                </li>
                                <li className="list-group-item border-0 ps-0">
                                    Click "Save" to publish your new item
                                </li>
                            </ol>
                        </div>
                    </div>

                    {/* Editing Items */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <h3 className="h5 fw-semibold mb-3">Editing Existing Items</h3>
                            <ol className="list-group list-group-numbered list-group-flush">
                                <li className="list-group-item border-0 ps-0">
                                    Click on any item in your menu items list
                                </li>
                                <li className="list-group-item border-0 ps-0">
                                    Make your changes in the form
                                </li>
                                <li className="list-group-item border-0 ps-0">
                                    Click "Save" to update the item on your live website
                                </li>
                            </ol>
                        </div>
                    </div>

                    {/* Deleting Items */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <h3 className="h5 fw-semibold mb-3">Deleting Items</h3>
                            <ol className="list-group list-group-numbered list-group-flush">
                                <li className="list-group-item border-0 ps-0">
                                    Open the item you want to delete
                                </li>
                                <li className="list-group-item border-0 ps-0">
                                    Look for the delete button (usually a trash icon) in the top right
                                </li>
                                <li className="list-group-item border-0 ps-0">
                                    Confirm the deletion
                                </li>
                            </ol>
                        </div>
                    </div>
                </section>

                {/* Item Types */}
                <section className="mb-5">
                    <h2 className="h3 fw-bold mb-4">Menu Item Types</h2>

                    {/* Wine */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <h3 className="h5 fw-semibold mb-3">🍷 Wine</h3>
                            <p className="mb-3">For wine items, you'll see these additional fields:</p>
                            <ul className="list-unstyled ms-3">
                                <li className="mb-2">• <strong>Producer:</strong> The winery or producer name</li>
                                <li className="mb-2">• <strong>Vintage:</strong> The year the wine was produced</li>
                                <li className="mb-2">• <strong>Region:</strong> Where the wine is from (e.g., "Napa Valley, California")</li>
                                <li className="mb-2">• <strong>Varietal:</strong> The grape type (e.g., "Cabernet Sauvignon")</li>
                            </ul>
                        </div>
                    </div>

                    {/* Beer */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <h3 className="h5 fw-semibold mb-3">🍺 Beer</h3>
                            <p className="mb-3">For beer items, you'll see these additional fields:</p>
                            <ul className="list-unstyled ms-3">
                                <li className="mb-2">• <strong>Brewery:</strong> The brewery name</li>
                                <li className="mb-2">• <strong>Style:</strong> Type of beer (e.g., "IPA", "Stout", "Lager")</li>
                                <li className="mb-2">• <strong>ABV:</strong> Alcohol by volume percentage</li>
                                <li className="mb-2">• <strong>IBU:</strong> International Bitterness Units (optional)</li>
                            </ul>
                        </div>
                    </div>

                    {/* Cocktails */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <h3 className="h5 fw-semibold mb-3">🍸 Cocktails</h3>
                            <p className="mb-3">For cocktail items, you'll see these additional fields:</p>
                            <ul className="list-unstyled ms-3">
                                <li className="mb-2">• <strong>Base Spirit:</strong> The main spirit (e.g., "Vodka", "Rum", "Whiskey")</li>
                                <li className="mb-2">• <strong>Ingredients:</strong> List of ingredients in the cocktail</li>
                            </ul>
                        </div>
                    </div>

                    {/* Food */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <h3 className="h5 fw-semibold mb-3">🍽️ Food</h3>
                            <p className="mb-3">For food items, you'll see these additional fields:</p>
                            <ul className="list-unstyled ms-3">
                                <li className="mb-2">• <strong>Category:</strong> Type of dish (e.g., "Appetizer", "Entree", "Dessert")</li>
                                <li className="mb-2">• <strong>Dietary Info:</strong> Special diet tags (e.g., "Vegetarian", "Gluten-Free")</li>
                                <li className="mb-2">• <strong>Allergens:</strong> Common allergens present in the dish</li>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* Best Practices */}
                <section className="mb-5">
                    <h2 className="h3 fw-bold mb-4">Best Practices</h2>
                    <div className="alert alert-info border-start border-4 border-info">
                        <ul className="list-unstyled mb-0">
                            <li className="mb-3">
                                <span className="text-info fw-bold me-2">✓</span>
                                <strong>Keep descriptions concise:</strong> 1-2 sentences work best
                            </li>
                            <li className="mb-3">
                                <span className="text-info fw-bold me-2">✓</span>
                                <strong>Update prices regularly:</strong> Changes appear instantly on your website
                            </li>
                            <li className="mb-3">
                                <span className="text-info fw-bold me-2">✓</span>
                                <strong>Use clear item names:</strong> Customers should know what they're ordering
                            </li>
                            <li className="mb-3">
                                <span className="text-info fw-bold me-2">✓</span>
                                <strong>Fill in all relevant fields:</strong> More information helps customers make decisions
                            </li>
                            <li className="mb-0">
                                <span className="text-info fw-bold me-2">✓</span>
                                <strong>Remove sold-out items:</strong> Or use the status field to mark them as unavailable
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Managing Multiple Locations */}
                <section className="mb-5">
                    <h2 className="h3 fw-bold mb-4">Managing Multiple Locations</h2>
                    <div className="card shadow-sm">
                        <div className="card-body">
                            <p className="mb-3">
                                If you manage multiple restaurant locations, each location has its own separate CMS.
                                You'll need to log into each location's profile separately to manage its menu.
                            </p>
                            <p className="mb-0">
                                To switch between locations, return to your main Nectr Studio profile page and
                                select the location you want to manage.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="mb-5">
                    <div className="card bg-light text-center">
                        <div className="card-body py-5">
                            <h2 className="h3 fw-bold mb-3">Need Help?</h2>
                            <p className="mb-4">
                                Can't find what you're looking for or need additional assistance?
                            </p>
                            <a
                                href="mailto:support@nectrstudio.com"
                                className="btn btn-lg text-white"
                                style={{ backgroundColor: '#2d5f5d' }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#234948'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#2d5f5d'}
                            >
                                Contact Support
                            </a>
                        </div>
                    </div>
                </section>

            </div>
        </div>
    );
};

export default DocsPage;